import * as actionTypes from '../../constants/POS/productConstants';
import { productService } from '../../services/POS/ProductService';
import { getAllowedCategoryIds } from '../../config/userCategoryMap';
 
 

// export const fetchProducts = () => async (dispatch) => {
//   dispatch({ type: actionTypes.FETCH_PRODUCTS_REQUEST });
//   try {
//     const products = await productService.getAllProducts();
//     const formattedProducts = await Promise.all(
//       products.map(async (product) => {
//         const imageUrl = await productService.getProductImage(product.IMAGEURL);
//         return {
//           id: product.PPROCODE,
//           name: product.P_DESC,
//           price: parseFloat(product.PSELLPRICE) || 0, 
//           discountType: product.Dis_Type,
//           discountValue: parseFloat(product.Dis_Value) || 0,
//           discountedPrice: parseFloat(product.Dis_APrice) || parseFloat(product.PSELLPRICE) || 0,
//           markedPrice: parseFloat(product.PMarkedPRICE) || parseFloat(product.PSELLPRICE) || 0,
//           category: product.P_TYP || 'Uncategorized',
//           subCategory: product.Sub_CatID,
//           stock: parseFloat(product.P_BALQTY) || 0,
//           sku: product.P_SKU,
//           barcode:  product.P_BarCode || product.P_SKU,
//           image: imageUrl,
//           Whcode: product.PWHCODE,
//         };
//       })
//     );

//     dispatch({
//       type: actionTypes.FETCH_PRODUCTS_SUCCESS,
//       payload: formattedProducts
//     });
//   } catch (error) {
//     dispatch({
//       type: actionTypes.FETCH_PRODUCTS_FAILURE,
//       payload: error.message
//     });
//   }
// };




// ─── fetchProducts ───────────────────────────────────────────────────────────
// Fetches all products and filters them by the logged-in user's allowed
// category IDs. Matching is done via P_MID (product) === Main_CatID (category).
export const fetchProducts = (username) => async (dispatch) => {
  dispatch({ type: actionTypes.FETCH_PRODUCTS_REQUEST });
  try {
    const [products, allCategories] = await Promise.all([
      productService.getAllProducts(),
      productService.getAllCategories(),
    ]);

    // Resolve which Main_CatIDs this user is allowed to see
    const allowedIds = getAllowedCategoryIds(allCategories, username);

    // Filter products: keep only those whose P_MID is in the allowed set
    const filteredRaw = allowedIds === null
      ? products
      : products.filter((product) =>
          allowedIds.has(String(product.P_MID))
        );

    const formattedProducts = await Promise.all(
      filteredRaw.map(async (product) => {
        const imageUrl = await productService.getProductImage(product.IMAGEURL);

        const hasMultipleBatches = product.BatchDetailsModel && product.BatchDetailsModel.length > 1;
        const firstBatch = product.BatchDetailsModel?.[0];

        return {
          id: product.PPROCODE,
          productId: product.PPROCODE,
          name: product.P_DESC,
          price: parseFloat(firstBatch?.PSELLPRICE || product.PSELLPRICE) || 0,
          discountType: firstBatch?.PromoModel?.[0]?.Dis_Type || product.Dis_Type,
          discountValue: parseFloat(firstBatch?.PromoModel?.[0]?.Dis_Value || product.Dis_Value) || 0,
          discountedPrice: parseFloat(firstBatch?.ActualSellPrice || product.Dis_APrice || product.PSELLPRICE) || 0,
          markedPrice: parseFloat(firstBatch?.PMarkedPRICE || product.PMarkedPRICE || product.PSELLPRICE) || 0,
          category: product.P_TYP || 'Uncategorized',
          mainCatId: String(product.P_MID),
          subCategory: product.Sub_CatID,
          stock: parseFloat(product.Total_BLQTY) || 0,
          sku: product.P_SKU,
          barcode: product.P_BarCode || product.P_SKU,
          image: imageUrl,
          Whcode: firstBatch?.PWHCODE || product.PWHCODE,
          hasMultipleBatches: hasMultipleBatches,
          totalBatches: product.BatchDetailsModel?.length || 1,
          allBatches: product.BatchDetailsModel?.map((batch) => ({
            id: `${product.PPROCODE}_B${batch.PBATID}`,
            productId: product.PPROCODE,
            batchId: batch.PBATID,
            name: product.P_DESC,
            price: parseFloat(batch.PSELLPRICE) || 0,
            discountType: batch.PromoModel?.[0]?.Dis_Type || null,
            discountValue: parseFloat(batch.PromoModel?.[0]?.Dis_Value) || 0,
            discountedPrice: parseFloat(batch.ActualSellPrice) || parseFloat(batch.PSELLPRICE) || 0,
            markedPrice: parseFloat(batch.PMarkedPRICE) || parseFloat(batch.PSELLPRICE) || 0,
            stock: parseFloat(batch.P_BALQTY) || 0,
            sku: product.P_SKU,
            barcode: product.P_BarCode || product.P_SKU,
            image: imageUrl,
            Whcode: batch.PWHCODE,
            expiryDate: batch.PEXDATE,
            batchDetails: batch,
            isBatchProduct: true
          })) || [],
          originalProduct: product
        };
      })
    );

    dispatch({
      type: actionTypes.FETCH_PRODUCTS_SUCCESS,
      payload: formattedProducts
    });
  } catch (error) {
    dispatch({
      type: actionTypes.FETCH_PRODUCTS_FAILURE,
      payload: error.message
    });
  }
};


// ─── fetchCategories ─────────────────────────────────────────────────────────
// Fetches all categories and filters them by the logged-in user's allowed
// category name keywords, resolved to Main_CatID.
export const fetchCategories = (username) => async (dispatch) => {
  dispatch({ type: actionTypes.FETCH_CATEGORIES_REQUEST });
  try {
    const categories = await productService.getAllCategories();

    // Resolve allowed IDs for this user
    const allowedIds = getAllowedCategoryIds(categories, username);

    // Filter: keep only categories whose Main_CatID is in the allowed set
    const filteredCategories = allowedIds === null
      ? categories
      : categories.filter((cat) => allowedIds.has(String(cat.Main_CatID)));

    const formattedCategories = filteredCategories.map(cat => ({
      id: cat.Main_CatID,
      label: cat.Main_CatName,
      icon: getCategoryIcon(cat.Main_CatName),
      subcategories: []
    }));

    // Prepend the "All Products" entry
    formattedCategories.unshift({
      id: 'all',
      icon: 'fas fa-boxes',
      label: 'All Products',
      subcategories: []
    });

    dispatch({
      type: actionTypes.FETCH_CATEGORIES_SUCCESS,
      payload: formattedCategories
    });
  } catch (error) {
    dispatch({
      type: actionTypes.FETCH_CATEGORIES_FAILURE,
      payload: error.message
    });
  }
};

export const fetchSubcategories = (mainId) => async (dispatch) => {
  if (mainId === 'all') return;
  dispatch({ type: actionTypes.FETCH_SUBCATEGORIES_REQUEST });
  try {
    const subcategories = await productService.getSubcategoriesByMainId(mainId);
    const formattedSubcategories = subcategories.map(sub => ({
      id: sub.Sub_CatID,
      label: sub.Sub_CatName
    }));
    dispatch({
      type: actionTypes.FETCH_SUBCATEGORIES_SUCCESS,
      payload: { mainId, subcategories: formattedSubcategories }
    });
  } catch (error) {
    dispatch({
      type: actionTypes.FETCH_SUBCATEGORIES_FAILURE,
      payload: error.message
    });
  }
};
 
// export const fetchProductsBySubcategory = (subId) => async (dispatch) => {
//   try {
//     const products = await productService.getProductsBySubId(subId);
//     const formattedProducts = await Promise.all(
//       products.map(async (product) => {
//         const imageUrl = await productService.getProductImage(product.IMAGEURL);
//         return {
//           id: product.PPROCODE,
//           name: product.P_DESC,
//           price: parseFloat(product.PSELLPRICE) || 0,
//           category: product.P_TYP || 'Uncategorized',
//           subCategory: product.Sub_CatID,
//           Whcode: product.PWHCODE,
//           stock: parseFloat(product.P_BALQTY) || 0,
//           sku: product.P_SKU,
//           barcode:  product.P_BarCode || product.P_SKU,
//           image: imageUrl,
//         };
//       })
//     );
//     dispatch({
//       type: actionTypes.SET_PRODUCTS,
//       payload: formattedProducts
//     });
//   } catch (error) {

//   }
// };



export const fetchProductsBySubcategory = (subId) => async (dispatch) => {
  try {
    const products = await productService.getProductsBySubId(subId);
    const formattedProducts = await Promise.all(
      products.map(async (product) => {
        const imageUrl = await productService.getProductImage(product.IMAGEURL);
        
        const hasMultipleBatches = product.BatchDetailsModel && product.BatchDetailsModel.length > 1;
        const firstBatch = product.BatchDetailsModel?.[0];
        
        return {
          id: product.PPROCODE,
          productId: product.PPROCODE,
          name: product.P_DESC,
          price: parseFloat(firstBatch?.PSELLPRICE || product.PSELLPRICE) || 0,
          discountType: firstBatch?.PromoModel?.[0]?.Dis_Type || product.Dis_Type,
          discountValue: parseFloat(firstBatch?.PromoModel?.[0]?.Dis_Value || product.Dis_Value) || 0,
          discountedPrice: parseFloat(firstBatch?.ActualSellPrice || product.Dis_APrice || product.PSELLPRICE) || 0,
          markedPrice: parseFloat(firstBatch?.PMarkedPRICE || product.PMarkedPRICE || product.PSELLPRICE) || 0,
          category: product.P_TYP || 'Uncategorized',
          subCategory: product.Sub_CatID,
          stock: parseFloat(product.Total_BLQTY) || 0,
          sku: product.P_SKU,
          barcode: product.P_BarCode || product.P_SKU,
          image: imageUrl,
          Whcode: firstBatch?.PWHCODE || product.PWHCODE,
          hasMultipleBatches: hasMultipleBatches,
          totalBatches: product.BatchDetailsModel?.length || 1,
          allBatches: product.BatchDetailsModel?.map((batch, index) => ({
            id: `${product.PPROCODE}_B${batch.PBATID}`,
            productId: product.PPROCODE,
            batchId: batch.PBATID,
            name: product.P_DESC,
            price: parseFloat(batch.PSELLPRICE) || 0,
            discountType: batch.PromoModel?.[0]?.Dis_Type || null,
            discountValue: parseFloat(batch.PromoModel?.[0]?.Dis_Value) || 0,
            discountedPrice: parseFloat(batch.ActualSellPrice) || parseFloat(batch.PSELLPRICE) || 0,
            markedPrice: parseFloat(batch.PMarkedPRICE) || parseFloat(batch.PSELLPRICE) || 0,
            stock: parseFloat(batch.P_BALQTY) || 0,
            sku: product.P_SKU,
            barcode: product.P_BarCode || product.P_SKU,
            image: imageUrl,
            Whcode: batch.PWHCODE,
            expiryDate: batch.PEXDATE,
            batchDetails: batch,
            isBatchProduct: true
          })) || [],
          originalProduct: product
        };
      })
    );
    
    dispatch({
      type: actionTypes.SET_PRODUCTS,
      payload: formattedProducts
    });
  } catch (error) {
  }
};






export const filterProducts = (categoryId) => ({
  type: actionTypes.FILTER_PRODUCTS,
  payload: categoryId
});
export const searchProducts = (query) => ({
  type: actionTypes.SEARCH_PRODUCTS,
  payload: query
});
export const setCurrentCategory = (categoryId) => ({
  type: actionTypes.SET_CURRENT_CATEGORY,
  payload: categoryId
});
export const setCurrentSubcategory = (subcategoryId) => ({
  type: actionTypes.SET_CURRENT_SUBCATEGORY,
  payload: subcategoryId
});
const getCategoryIcon = (categoryName) => {
  const iconMap = {
    'electronics': 'fas fa-microchip',
    'computers': 'fas fa-laptop',
    'phones': 'fas fa-mobile-alt',
    'accessories': 'fas fa-headphones',
    'components': 'fas fa-microchip',
  };
  const lowerName = categoryName.toLowerCase();
  return iconMap[lowerName] || 'fas fa-box';
};
 
export const refreshProducts = () => ({
  type: actionTypes.REFRESH_PRODUCTS
});